/**
 * NinjaBadge — 8 distinct cartoon ninja avatars, one per belt/rank.
 * Each ninja has unique headwear, accessories, pose, and expression so
 * they read differently at a glance (not just color-swapped).
 */

const BELT = {
  1: "#e5e7eb", // white
  2: "#fbbf24", // yellow
  3: "#fb923c", // orange
  4: "#22c55e", // green
  5: "#3b82f6", // blue
  6: "#a855f7", // purple
  7: "#78350f", // brown
  8: "#0a0a0a", // black
};

const GLOW = {
  1: "rgba(226,232,240,0.4)",
  2: "rgba(251,191,36,0.5)",
  3: "rgba(251,146,60,0.5)",
  4: "rgba(34,197,94,0.55)",
  5: "rgba(59,130,246,0.55)",
  6: "rgba(168,85,247,0.55)",
  7: "rgba(120,53,15,0.55)",
  8: "rgba(34,197,94,0.7)",
};

const SKIN = "#f4c8a1";
const SUIT = "#0f172a";
const OUTLINE = "#020617";

// --- Level 1: Student Ninja — small, nervous, plain white headband ---
function StudentNinja() {
  return (
    <g>
      <path d="M 60,150 Q 60,110 100,110 Q 140,110 140,150 L 140,180 L 60,180 Z" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <rect x="58" y="150" width="84" height="10" fill={BELT[1]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="80" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <rect x="66" y="60" width="68" height="10" fill={BELT[1]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 132,66 L 148,58 L 148,74 Z" fill={BELT[1]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="88" cy="82" r="6" fill="#fff" stroke={OUTLINE} strokeWidth="2" />
      <circle cx="112" cy="82" r="6" fill="#fff" stroke={OUTLINE} strokeWidth="2" />
      <circle cx="88" cy="83" r="2.5" fill={OUTLINE} />
      <circle cx="112" cy="83" r="2.5" fill={OUTLINE} />
      <path d="M 92,100 Q 100,96 108,100" fill="none" stroke={OUTLINE} strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="55" cy="140" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="145" cy="140" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
    </g>
  );
}

// --- Level 2: EKG Apprentice — reading a scroll, yellow headband ---
function ApprenticeNinja() {
  return (
    <g>
      <path d="M 60,150 Q 60,108 100,108 Q 140,108 140,150 L 140,180 L 60,180 Z" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <rect x="58" y="150" width="84" height="10" fill={BELT[2]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="78" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <rect x="66" y="58" width="68" height="10" fill={BELT[2]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 62,63 L 46,52 L 50,72 Z" fill={BELT[2]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 82,80 Q 90,76 96,82" fill="none" stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" />
      <path d="M 104,82 Q 110,76 118,80" fill="none" stroke={OUTLINE} strokeWidth="3" strokeLinecap="round" />
      <path d="M 92,98 Q 100,104 108,98" fill="none" stroke={OUTLINE} strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="52" cy="152" rx="8" ry="12" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="148" cy="152" rx="8" ry="12" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <rect x="72" y="130" width="56" height="20" rx="4" fill="#fde68a" stroke={OUTLINE} strokeWidth="2.5" />
      <line x1="80" y1="138" x2="120" y2="138" stroke={OUTLINE} strokeWidth="1.5" />
      <line x1="80" y1="144" x2="115" y2="144" stroke={OUTLINE} strokeWidth="1.5" />
    </g>
  );
}

// --- Level 3: Shadow Genin — masked, holding a shuriken ---
function ShadowGenin() {
  return (
    <g>
      <path d="M 60,150 Q 60,110 100,110 Q 140,110 140,150 L 140,180 L 60,180 Z" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <rect x="58" y="150" width="84" height="10" fill={BELT[3]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="78" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 66,86 Q 100,72 134,86 L 134,108 Q 100,116 66,108 Z" fill={BELT[3]} stroke={OUTLINE} strokeWidth="2.5" />
      <rect x="66" y="58" width="68" height="9" fill={BELT[3]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 82,78 L 96,80" stroke={OUTLINE} strokeWidth="4" strokeLinecap="round" />
      <path d="M 104,80 L 118,78" stroke={OUTLINE} strokeWidth="4" strokeLinecap="round" />
      <ellipse cx="55" cy="138" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <g transform="translate(150,128) rotate(30)">
        <ellipse cx="0" cy="0" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      </g>
      <g transform="translate(168,116)">
        <path d="M 0,-13 L 4,-4 L 13,0 L 4,4 L 0,13 L -4,4 L -13,0 L -4,-4 Z" fill="#cbd5e1" stroke={OUTLINE} strokeWidth="2" />
        <circle cx="0" cy="0" r="2.5" fill={OUTLINE} />
      </g>
    </g>
  );
}

// --- Level 4: Rhythm Ninja — katana strapped to back ---
function RhythmNinja() {
  return (
    <g>
      <line x1="130" y1="45" x2="185" y2="100" stroke="#94a3b8" strokeWidth="7" strokeLinecap="round" />
      <line x1="130" y1="45" x2="185" y2="100" stroke={OUTLINE} strokeWidth="2" strokeLinecap="round" />
      <rect x="122" y="34" width="18" height="12" rx="2" fill="#78350f" stroke={OUTLINE} strokeWidth="2" transform="rotate(45 131 40)" />
      <path d="M 60,150 Q 60,110 100,110 Q 140,110 140,150 L 140,180 L 60,180 Z" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <rect x="58" y="150" width="84" height="10" fill={BELT[4]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="78" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 66,88 Q 100,74 134,88 L 134,110 Q 100,118 66,110 Z" fill={BELT[4]} stroke={OUTLINE} strokeWidth="2.5" />
      <rect x="66" y="56" width="68" height="10" fill={BELT[4]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 132,62 L 152,54 L 152,72 Z" fill={BELT[4]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 82,80 L 94,78 L 94,84 Z" fill={OUTLINE} />
      <path d="M 106,78 L 118,80 L 106,84 Z" fill={OUTLINE} />
      <ellipse cx="55" cy="140" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="145" cy="140" rx="8" ry="18" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
    </g>
  );
}

// --- Level 5: Ischemia Warrior — armor plates + heart insignia ---
function IschemiaWarrior() {
  return (
    <g>
      <path d="M 56,150 Q 56,108 100,108 Q 144,108 144,150 L 144,180 L 56,180 Z" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 72,118 L 128,118 L 132,148 L 68,148 Z" fill="#1e293b" stroke={OUTLINE} strokeWidth="2.5" />
      <circle cx="100" cy="132" r="10" fill={BELT[5]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 100,120 C 94,115 88,118 88,124 C 88,132 100,142 100,142 C 100,142 112,132 112,124 C 112,118 106,115 100,120 Z" fill="#ef4444" stroke={OUTLINE} strokeWidth="1.5" />
      <rect x="54" y="150" width="92" height="10" fill={BELT[5]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="78" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 66,88 Q 100,76 134,88 L 134,108 Q 100,116 66,108 Z" fill="#dc2626" stroke={OUTLINE} strokeWidth="2.5" />
      <rect x="66" y="58" width="68" height="10" fill={BELT[5]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 66,63 Q 100,68 134,63" fill="none" stroke="#dc2626" strokeWidth="3" />
      <path d="M 82,84 L 94,78 L 94,82 Z" fill={OUTLINE} />
      <path d="M 118,84 L 106,78 L 106,82 Z" fill={OUTLINE} />
      <ellipse cx="52" cy="140" rx="9" ry="20" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="148" cy="140" rx="9" ry="20" fill={SUIT} stroke={OUTLINE} strokeWidth="3" />
    </g>
  );
}

// --- Level 6: Arrhythmia Sensei — old wise ninja with staff & beard ---
function ArrhythmiaSensei() {
  return (
    <g>
      <line x1="30" y1="30" x2="30" y2="188" stroke="#78350f" strokeWidth="7" strokeLinecap="round" />
      <line x1="30" y1="30" x2="30" y2="188" stroke={OUTLINE} strokeWidth="2" strokeLinecap="round" />
      <circle cx="30" cy="26" r="8" fill={BELT[6]} stroke={OUTLINE} strokeWidth="2" />
      <path d="M 52,150 Q 52,108 100,108 Q 148,108 148,150 L 156,180 L 44,180 Z" fill={BELT[6]} stroke={OUTLINE} strokeWidth="3" />
      <rect x="50" y="152" width="100" height="10" fill="#c084fc" stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="78" r="34" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 60,80 Q 60,42 100,42 Q 140,42 140,80 L 140,68 Q 100,52 60,68 Z" fill={BELT[6]} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 82,82 Q 88,86 94,82" fill="none" stroke={OUTLINE} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M 106,82 Q 112,86 118,82" fill="none" stroke={OUTLINE} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M 78,74 Q 88,68 96,74" fill="none" stroke="#f8fafc" strokeWidth="4" strokeLinecap="round" />
      <path d="M 104,74 Q 112,68 122,74" fill="none" stroke="#f8fafc" strokeWidth="4" strokeLinecap="round" />
      <path d="M 82,102 Q 100,140 118,102 Q 118,124 100,132 Q 82,124 82,102 Z" fill="#f8fafc" stroke={OUTLINE} strokeWidth="2" />
    </g>
  );
}

// --- Level 7: Cardiology Jonin — samurai helm with horns + twin katanas ---
function CardiologyJonin() {
  return (
    <g>
      <line x1="18" y1="42" x2="182" y2="182" stroke="#94a3b8" strokeWidth="5" />
      <line x1="182" y1="42" x2="18" y2="182" stroke="#94a3b8" strokeWidth="5" />
      <rect x="8" y="34" width="18" height="12" fill="#78350f" stroke={OUTLINE} strokeWidth="2" transform="rotate(45 17 40)" />
      <rect x="174" y="34" width="18" height="12" fill="#78350f" stroke={OUTLINE} strokeWidth="2" transform="rotate(-45 183 40)" />
      <path d="M 56,155 Q 56,110 100,110 Q 144,110 144,155 L 144,182 L 56,182 Z" fill="#3f2410" stroke={OUTLINE} strokeWidth="3" />
      <rect x="70" y="120" width="60" height="14" fill="#78350f" stroke={OUTLINE} strokeWidth="2" />
      <rect x="66" y="138" width="68" height="10" fill="#78350f" stroke={OUTLINE} strokeWidth="2" />
      <rect x="62" y="152" width="76" height="10" fill={BELT[7]} stroke={OUTLINE} strokeWidth="2" />
      <circle cx="100" cy="80" r="30" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 60,72 Q 60,38 100,38 Q 140,38 140,72 L 140,66 Q 100,50 60,66 Z" fill="#3f2410" stroke={OUTLINE} strokeWidth="3" />
      <path d="M 60,60 Q 40,26 42,14 Q 58,30 68,52 Z" fill="#78350f" stroke={OUTLINE} strokeWidth="2.5" />
      <path d="M 140,60 Q 160,26 158,14 Q 142,30 132,52 Z" fill="#78350f" stroke={OUTLINE} strokeWidth="2.5" />
      <path d="M 72,92 Q 100,80 128,92 L 128,110 Q 100,116 72,110 Z" fill="#3f2410" stroke={OUTLINE} strokeWidth="2.5" />
      <circle cx="90" cy="82" r="4" fill="#ef4444" />
      <circle cx="110" cy="82" r="4" fill="#ef4444" />
      <path d="M 82,80 L 96,76 L 96,84 Z" fill={OUTLINE} />
      <path d="M 118,80 L 104,76 L 104,84 Z" fill={OUTLINE} />
    </g>
  );
}

// --- Level 8: Cardiology Kage — kage hat, glowing eyes, heart insignia, aura ---
function CardiologyKage() {
  return (
    <g>
      <circle cx="100" cy="105" r="92" fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.4" />
      <circle cx="100" cy="105" r="82" fill="none" stroke="#22c55e" strokeWidth="1" opacity="0.25" />
      <path d="M 40,155 Q 40,108 100,108 Q 160,108 160,155 L 168,184 L 32,184 Z" fill="#020617" stroke={OUTLINE} strokeWidth="3" />
      <path d="M 40,155 Q 40,108 100,108 Q 160,108 160,155" fill="none" stroke="#eab308" strokeWidth="2" />
      <rect x="38" y="156" width="124" height="10" fill="#eab308" stroke={OUTLINE} strokeWidth="2" />
      <path d="M 100,120 C 94,112 84,116 84,124 C 84,134 100,144 100,144 C 100,144 116,134 116,124 C 116,116 106,112 100,120 Z" fill="#22c55e" stroke={OUTLINE} strokeWidth="2" />
      <path d="M 86,130 L 92,130 L 95,124 L 100,138 L 105,124 L 108,130 L 114,130" fill="none" stroke="#020617" strokeWidth="1.5" />
      <circle cx="100" cy="78" r="30" fill={SKIN} stroke={OUTLINE} strokeWidth="3" />
      <path d="M 36,60 L 100,10 L 164,60 L 152,66 L 100,32 L 48,66 Z" fill="#020617" stroke={OUTLINE} strokeWidth="3" />
      <circle cx="100" cy="42" r="9" fill="#22c55e" stroke={OUTLINE} strokeWidth="2" />
      <path d="M 94,44 L 100,36 L 106,44 L 108,48 L 102,52 L 100,46 L 98,52 L 92,48 Z" fill="#020617" />
      <ellipse cx="100" cy="66" rx="64" ry="9" fill="#020617" stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="100" cy="66" rx="64" ry="9" fill="none" stroke="#eab308" strokeWidth="1.5" />
      <circle cx="90" cy="82" r="5" fill="#22c55e" />
      <circle cx="110" cy="82" r="5" fill="#22c55e" />
      <circle cx="90" cy="82" r="2" fill="#f0fdf4" />
      <circle cx="110" cy="82" r="2" fill="#f0fdf4" />
      <path d="M 74,94 Q 100,84 126,94 L 126,108 Q 100,114 74,108 Z" fill="#020617" stroke={OUTLINE} strokeWidth="2" />
    </g>
  );
}

const NINJAS = {
  1: StudentNinja,
  2: ApprenticeNinja,
  3: ShadowGenin,
  4: RhythmNinja,
  5: IschemiaWarrior,
  6: ArrhythmiaSensei,
  7: CardiologyJonin,
  8: CardiologyKage,
};

export default function NinjaBadge({ level = 1, size = 100, locked = false, glow = false, className = "" }) {
  const N = NINJAS[level] || StudentNinja;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      style={
        glow && !locked
          ? { filter: `drop-shadow(0 0 14px ${GLOW[level]})` }
          : undefined
      }
      aria-hidden="true"
    >
      {locked ? (
        <g opacity="0.35">
          <N />
          {/* Lock overlay */}
          <circle cx="100" cy="105" r="98" fill="#0a0a0a" opacity="0.55" />
          <rect x="82" y="92" width="36" height="30" rx="3" fill="#334155" stroke="#020617" strokeWidth="2.5" />
          <path d="M 88,92 v-10 a12 12 0 0 1 24 0 v10" fill="none" stroke="#334155" strokeWidth="4" />
        </g>
      ) : (
        <N />
      )}
    </svg>
  );
}

export function beltColor(level) {
  return { belt: BELT[level] || BELT[1], glow: GLOW[level] || GLOW[1] };
}
